import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { StoryItem } from '../types';
import { 
  X, 
  Pause,
  ArrowUpRight,
  ShoppingBag,
  ExternalLink,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { DEFAULT_STORIES } from '../data/initialData';

export const InstagramStories: React.FC = () => {
  const { 
    pagesContent, 
    products, 
    setSelectedProduct, 
    setCurrentView, 
    setActiveCategoryFilter, 
    language 
  } = useStore();

  const storiesSettings = pagesContent.storiesSettings || DEFAULT_STORIES;
  const items = storiesSettings.items && storiesSettings.items.length > 0 
    ? storiesSettings.items 
    : DEFAULT_STORIES.items;

  // Active Story & Slide State
  const [activeStoryIndex, setActiveStoryIndex] = useState<number | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const durationSec = Math.max(storiesSettings.viewDuration || 5, 2);
  const durationMs = durationSec * 1000;

  // Don't render tray if disabled in admin
  if (storiesSettings.enabled === false) {
    return null;
  }

  const currentStory: StoryItem | null = activeStoryIndex !== null ? items[activeStoryIndex] : null;

  // Get all images/slides for the active story (supports multiple images or single fallback)
  const getStoryImages = (story: StoryItem | null): string[] => {
    if (!story) return [];
    if (story.images && story.images.length > 0) {
      return story.images.filter(Boolean);
    }
    return story.image ? [story.image] : ['/hero-image-coveristic-mobile.webp'];
  };

  const activeStoryImages = getStoryImages(currentStory);
  const totalSlides = activeStoryImages.length;

  // Auto advance slide or advance to next story
  useEffect(() => {
    if (activeStoryIndex === null || !currentStory) {
      setProgress(0);
      return;
    }

    if (isPaused) {
      return;
    }

    const stepMs = 50;
    const increment = (stepMs / durationMs) * 100;

    progressIntervalRef.current = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          // Check if there are more image slides in this same story
          if (currentSlideIndex < totalSlides - 1) {
            setCurrentSlideIndex(curr => curr + 1);
            return 0;
          } else {
            // Finished all slides of this story -> advance to next story item
            if (activeStoryIndex < items.length - 1) {
              setActiveStoryIndex(activeStoryIndex + 1);
              setCurrentSlideIndex(0);
              return 0;
            } else {
              // Finished all stories -> close viewer
              setActiveStoryIndex(null);
              setCurrentSlideIndex(0);
              return 0;
            }
          }
        }
        return next;
      });
    }, stepMs);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [activeStoryIndex, currentSlideIndex, totalSlides, isPaused, durationMs, items.length, currentStory]);

  // Reset slide and progress when changing active story index
  useEffect(() => {
    setProgress(0);
  }, [activeStoryIndex, currentSlideIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (activeStoryIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveStoryIndex(null);
      } else if (e.key === 'ArrowRight' || e.key === 'Space') {
        handleNextSlideOrStory();
      } else if (e.key === 'ArrowLeft') {
        handlePrevSlideOrStory();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeStoryIndex, currentSlideIndex, totalSlides, items.length]);

  const handleOpenStory = (index: number) => {
    setActiveStoryIndex(index);
    setCurrentSlideIndex(0);
    setProgress(0);
    setIsPaused(false);
  };

  const handleNextSlideOrStory = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(curr => curr + 1);
      setProgress(0);
    } else if (activeStoryIndex !== null && activeStoryIndex < items.length - 1) {
      setActiveStoryIndex(activeStoryIndex + 1);
      setCurrentSlideIndex(0);
      setProgress(0);
    } else {
      setActiveStoryIndex(null);
      setCurrentSlideIndex(0);
      setProgress(0);
    }
  };

  const handlePrevSlideOrStory = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(curr => curr - 1);
      setProgress(0);
    } else if (activeStoryIndex !== null && activeStoryIndex > 0) {
      const prevIdx = activeStoryIndex - 1;
      const prevImages = getStoryImages(items[prevIdx]);
      setActiveStoryIndex(prevIdx);
      setCurrentSlideIndex(Math.max(0, prevImages.length - 1));
      setProgress(0);
    }
  };

  // Button Action Handler (Category filter, Custom URL, or Linked Product)
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentStory) return;

    // Close the story viewer
    setActiveStoryIndex(null);

    // 1. Linked to a Category
    if (currentStory.ctaCategory && currentStory.ctaCategory !== 'none') {
      setSelectedProduct(null);
      setActiveCategoryFilter(currentStory.ctaCategory);
      setCurrentView('shop');
      window.scrollTo({ top: 400, behavior: 'smooth' });
      return;
    }

    // 2. Custom URL or path
    if (currentStory.ctaUrl && currentStory.ctaUrl.trim()) {
      const url = currentStory.ctaUrl.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // Internal route like /custom-print, /offers, /about
        setSelectedProduct(null);
        if (url === '/custom-print' || url === 'custom-print') {
          setCurrentView('custom-print');
        } else if (url === '/offers' || url === 'offers') {
          setCurrentView('offers');
        } else if (url === '/about' || url === 'about') {
          setCurrentView('about');
        } else if (url === '/shop' || url === 'shop') {
          setCurrentView('shop');
        } else {
          // If it's a category path e.g. /category/phone-cases
          if (url.startsWith('/category/')) {
            const cat = url.replace('/category/', '');
            setActiveCategoryFilter(cat);
            setCurrentView('shop');
          } else {
            window.location.href = url;
          }
        }
      }
      return;
    }

    // 3. Linked to a catalog product
    if (currentStory.productId) {
      const prod = products.find(p => p.id === currentStory.productId || p.slug === currentStory.productId);
      if (prod) {
        setSelectedProduct(prod);
        return;
      }
    }

    // 4. Default WhatsApp inquiry
    const waText = encodeURIComponent(`Hi Coveristic! I saw your story "${currentStory.title}" and would like to order or inquire.`);
    window.open(`https://wa.me/96181773349?text=${waText}`, '_blank', 'noopener,noreferrer');
  };

  const activeImage = activeStoryImages[currentSlideIndex] || currentStory?.image || '/hero-image-coveristic-mobile.webp';

  return (
    <div className="w-full bg-white border-b border-neutral-200 py-4 sm:py-6 select-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Tray Header */}
        <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4 text-center">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E21B24] animate-pulse" />
            <span className="text-xs sm:text-sm font-black uppercase tracking-wider text-neutral-800 font-heading">
              {storiesSettings.label || (language === 'ar' ? 'قصص كوفرستك وتصاميم مميزة' : 'COVERISTIC STORIES & DROPS')}
            </span>
          </div>
          <span className="text-neutral-300 select-none">•</span>
          <span className="text-[11px] sm:text-xs text-neutral-400 font-bold uppercase tracking-wider">
            {language === 'ar' ? 'اضغط للمعاينة' : 'Tap to View'}
          </span>
        </div>

        {/* Stories Horizontal Tray */}
        <div className="w-full overflow-x-auto no-scrollbar py-2 flex items-center justify-start sm:justify-center">
          <div className="flex items-center justify-center gap-4 sm:gap-7 md:gap-9 min-w-max mx-auto px-4">
            {items.map((story: StoryItem, idx: number) => {
              const storyImages = getStoryImages(story);
              const previewImg = story.image || storyImages[0] || '/hero-image-coveristic-mobile.webp';
              const multiCount = storyImages.length;

              return (
                <button
                  key={story.id || idx}
                  type="button"
                  onClick={() => handleOpenStory(idx)}
                  className="group flex flex-col items-center gap-2 shrink-0 cursor-pointer focus:outline-none transition-transform active:scale-95 text-center"
                >
                  {/* Radiant Gradient Ring */}
                  <div className="relative p-[3px] sm:p-1 rounded-full bg-gradient-to-tr from-[#E21B24] via-rose-500 to-amber-500 shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200">
                    <div className="p-0.5 sm:p-1 bg-white rounded-full">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-neutral-100 relative">
                        <img
                          src={previewImg}
                          alt={story.title}
                          width={112}
                          height={112}
                          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/hero-image-coveristic-mobile.webp';
                          }}
                        />

                        {/* Multiple Images Indicator Badge */}
                        {multiCount > 1 && (
                          <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full border border-white/40 shadow-xs flex items-center gap-0.5">
                            +{multiCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Story Title */}
                  <span className="text-xs sm:text-sm font-bold text-neutral-800 max-w-[84px] sm:max-w-[104px] md:max-w-[116px] truncate text-center group-hover:text-[#E21B24] transition-colors">
                    {story.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Coveristic Story Fullscreen Viewer Modal */}
      {activeStoryIndex !== null && currentStory && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 animate-fadeIn"
          onClick={() => setActiveStoryIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          {/* Story Card Container */}
          <div
            className="relative w-full max-w-md sm:max-w-lg md:max-w-xl h-[88vh] max-h-[820px] rounded-2xl overflow-hidden shadow-2xl bg-neutral-950 border border-neutral-800 flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={() => setIsPaused(true)}
            onMouseUp={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            {/* Background Story Image Slide */}
            <div className="absolute inset-0 z-0">
              <img
                key={activeImage}
                src={activeImage}
                alt={`${currentStory.title} - Slide ${currentSlideIndex + 1}`}
                className="w-full h-full object-cover select-none transition-opacity duration-300"
                draggable={false}
              />
              {/* Subtle top & bottom gradients for contrast */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-transparent to-black/85 pointer-events-none" />
            </div>

            {/* Top Navigation & Slide Progress Bars */}
            <div className="relative z-10 p-3 sm:p-4 space-y-3">
              {/* Progress Bar Segments - One per image in this active story */}
              <div className="flex items-center gap-1.5 w-full">
                {activeStoryImages.map((_, i: number) => {
                  let fillPercent = 0;
                  if (i < currentSlideIndex) fillPercent = 100;
                  else if (i === currentSlideIndex) fillPercent = progress;
                  else fillPercent = 0;

                  return (
                    <div key={i} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white transition-all duration-75 ease-linear rounded-full"
                        style={{ width: `${fillPercent}%` }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Story Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white shadow-xs shrink-0 bg-white">
                    <img
                      src={currentStory.image || activeImage}
                      alt={currentStory.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-black text-white font-heading leading-tight flex items-center gap-1.5">
                      <span>{currentStory.title}</span>
                      {currentStory.badge && (
                        <span className="text-[9px] font-black uppercase px-1.5 py-0.2 bg-[#E21B24] text-white rounded-xs">
                          {currentStory.badge}
                        </span>
                      )}
                    </h4>
                    <span className="text-[10px] text-neutral-300 font-medium flex items-center gap-1">
                      <span>Coveristic</span>
                      {totalSlides > 1 && (
                        <span>• Slide {currentSlideIndex + 1} of {totalSlides}</span>
                      )}
                      <span>• Story {activeStoryIndex + 1}/{items.length}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isPaused && (
                    <span className="flex items-center gap-1 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/20">
                      <Pause className="w-2.5 h-2.5" />
                      <span>Paused</span>
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => setActiveStoryIndex(null)}
                    className="p-1.5 bg-black/40 hover:bg-black/80 text-white rounded-full transition-colors cursor-pointer"
                    title="Close Story (ESC)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Middle Tap Areas (Left 35% goes previous slide, Right 65% goes next slide) */}
            <div className="relative z-10 flex-1 flex">
              <div
                className="w-[35%] h-full cursor-pointer flex items-center justify-start p-2 opacity-0 hover:opacity-70 transition-opacity"
                onClick={handlePrevSlideOrStory}
                title="Previous Slide"
              >
                <button
                  type="button"
                  className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 hidden sm:block"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>

              <div
                className="w-[65%] h-full cursor-pointer flex items-center justify-end p-2 opacity-0 hover:opacity-70 transition-opacity"
                onClick={handleNextSlideOrStory}
                title="Next Slide"
              >
                <button
                  type="button"
                  className="p-2 rounded-full bg-black/50 text-white hover:bg-black/80 hidden sm:block"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Bottom Content & Custom Action Button (if configured/enabled) */}
            {(currentStory.buttonEnabled || currentStory.ctaCategory || currentStory.ctaUrl || currentStory.subtitle) && (
              <div className="relative z-10 p-4 sm:p-5 space-y-2.5">
                {currentStory.subtitle && (
                  <p className="text-xs sm:text-sm font-semibold text-white/95 drop-shadow-md leading-relaxed text-center sm:text-left">
                    {currentStory.subtitle}
                  </p>
                )}

                {/* Optional Action Button configured with custom category, custom URL, or product */}
                {(currentStory.buttonEnabled !== false && (currentStory.ctaCategory || currentStory.ctaUrl || currentStory.ctaText || currentStory.productId)) && (
                  <button
                    type="button"
                    onClick={handleButtonClick}
                    className="w-full flex items-center justify-center gap-2 bg-[#E21B24] hover:bg-[#c9141c] text-white py-3 px-5 rounded-md font-black text-xs sm:text-sm uppercase tracking-wider shadow-xl transition-transform active:scale-95 cursor-pointer"
                  >
                    {currentStory.ctaCategory ? (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        <span>{currentStory.ctaText || `Browse ${currentStory.title}`}</span>
                      </>
                    ) : currentStory.ctaUrl ? (
                      <>
                        <ExternalLink className="w-4 h-4" />
                        <span>{currentStory.ctaText || 'Learn More & Order'}</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        <span>{currentStory.ctaText || 'View Details'}</span>
                      </>
                    )}
                    <ArrowUpRight className="w-4 h-4 ml-0.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

